import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { formatMoney, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Select, Spinner, TextInput,
} from '@shop/ui'

type Supplier = { id: string; nameAr: string; isActive: boolean }

type Warehouse = { id: string; nameAr: string; kind: string; isActive: boolean }

type Status = 'DRAFT' | 'POSTED' | 'REVERSED'

/**
 * نوعا الفاتورة:
 * - <b>receipt</b> فاتورةُ مشترياتٍ مباشرة — تُدخل البضاعةَ وكلفتَها وذمّتَها معًا.
 * - <b>cost</b> فاتورةُ كلفة — تُسعّر بضاعةً استُلمت قبلها بلا كلفة، ولا تُدخل كمّيّة.
 */
type Kind = 'receipt' | 'cost'

type ReceiptSummary = {
  id: string
  receiptNo: string
  supplierId: string
  supplierName: string
  warehouseId: string
  receiptDate: string
  supplierInvoiceNo: string | null
  supplierInvoiceDate: string | null
  status: Status
  netTotal: number
  notes: string | null
}

type ReceiptDetail = {
  header: ReceiptSummary
  lines: { id: string; variantId: string; productName: string; qty: number; unitCost: number; netAmount: number }[]
}

type CostSummary = {
  id: string
  invoiceNo: string
  status: Status
  supplierId: string
  supplierName: string
  supplierInvoiceNo: string | null
  supplierInvoiceDate: string | null
  total: number
  createdAt: string
  notes: string | null
  reverseReason: string | null
}

type CostDetail = {
  header: CostSummary
  lines: { id: string; variantId: string; productName: string; warehouseId: string; qty: number; unitCost: number; lineTotal: number }[]
}

/** الفاتورةُ بشكلٍ واحدٍ للنوعين — والشاشةُ لا تفرّق إلّا عند الحفظ. */
type Bill = {
  kind: Kind
  id: string
  no: string
  status: Status
  supplierId: string
  supplierName: string
  warehouseId: string | null
  invoiceNo: string | null
  date: string
  total: number
  notes: string | null
  lines: { id: string; variantId: string; name: string; qty: number; unitCost: number; total: number; warehouseId: string | null }[]
}

const fromReceipt = (d: ReceiptDetail): Bill => ({
  kind: 'receipt',
  id: d.header.id,
  no: d.header.receiptNo,
  status: d.header.status,
  supplierId: d.header.supplierId,
  supplierName: d.header.supplierName,
  warehouseId: d.header.warehouseId,
  invoiceNo: d.header.supplierInvoiceNo,
  date: d.header.supplierInvoiceDate ?? d.header.receiptDate,
  total: d.header.netTotal,
  notes: d.header.notes,
  lines: d.lines.map((l) => ({
    id: l.id, variantId: l.variantId, name: l.productName, qty: l.qty, unitCost: l.unitCost, total: l.netAmount, warehouseId: null,
  })),
})

const fromCost = (d: CostDetail): Bill => ({
  kind: 'cost',
  id: d.header.id,
  no: d.header.invoiceNo || '—',
  status: d.header.status,
  supplierId: d.header.supplierId,
  supplierName: d.header.supplierName,
  warehouseId: d.lines[0]?.warehouseId ?? null,
  invoiceNo: d.header.supplierInvoiceNo,
  date: d.header.supplierInvoiceDate ?? d.header.createdAt.slice(0, 10),
  total: d.header.total,
  notes: [d.header.notes, d.header.reverseReason && `سببُ الإلغاء: ${d.header.reverseReason}`].filter(Boolean).join(' — ') || null,
  lines: d.lines.map((l) => ({
    id: l.id, variantId: l.variantId, name: l.productName, qty: l.qty, unitCost: l.unitCost, total: l.lineTotal, warehouseId: l.warehouseId,
  })),
})

type CatalogItem = { variantId: string; sku: string; productName: string; label: string; barcodes: string[] }

/** سطرٌ على الشاشة — والكمّيّةُ والكلفةُ نصٌّ حتّى يُرسَل. */
type Draft = { variantId: string; name: string; qty: string; cost: string }

const STATUS: Record<Status, { label: string; tone: string }> = {
  DRAFT: { label: 'مسوّدة', tone: 'text-warn' },
  POSTED: { label: 'مُرحَّلة', tone: 'text-ok' },
  REVERSED: { label: 'ملغاة', tone: 'text-ink-soft line-through' },
}

const KIND_LABEL: Record<Kind, string> = { receipt: 'مشتريات', cost: 'تسعير' }

const base = (kind: Kind) => (kind === 'receipt' ? '/api/v1/receipts' : '/api/v1/cost-invoices')

const path = (kind: Kind, id: string) => (kind === 'receipt' ? `/purchase-bills/${id}` : `/purchase-bills/cost/${id}`)

function today(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(new Date())
}

function normalize(text: string): string {
  return text
    .replace(/[ً-ْـ]/g, '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .toLowerCase()
    .trim()
}

/**
 * فواتيرُ المشتريات — <b>طلبُ المالك ٢٠٢٦-١٠-٢٤</b>: «ابنيلي شاشة لادخال فواتير
 * المشتريات مباشرة وتعديل».
 *
 * <p>
 * الفاتورةُ هنا تُدخل البضاعةَ وكلفتَها وذمّةَ المورّد في خطوةٍ واحدة: مورّد، رقمُ
 * فاتورته وتاريخُها، والأصنافُ بكمّيّاتها وأسعارها — ثمّ «رحّل». وتظهر في القائمة
 * فواتيرُ الكلفة («تسعير») أيضًا لتُعدَّل من المكان نفسِه.
 * </p>
 *
 * <p>
 * ⚠ <b>والتعديلُ بعد الترحيل عكسٌ ثمّ نسخة</b>: «عدّل» تُلغي الفاتورةَ وتفتح
 * نسختَها مسوّدةً بسطورها لتُصحَّح وتُرحَّل. والأصلُ يبقى في القائمة «ملغاة» —
 * فلا يضيع ما كُتب.
 * </p>
 */
export default function PurchaseBills({ kind = 'receipt' }: { kind?: Kind }) {
  const { id } = useParams()

  return id ? <Editor kind={kind} id={id === 'new' ? null : id} /> : <List />
}

// ── القائمة ─────────────────────────────────────────────────────────────────

type Row = { kind: Kind; id: string; no: string; supplierName: string; invoiceNo: string | null; date: string; total: number; status: Status }

function List() {
  const navigate = useNavigate()
  const { can } = useAuth()
  const [status, setStatus] = useState<'' | Status>('')

  const query = status ? `?status=${status}` : ''

  const receipts = useQuery({
    queryKey: ['purchase-bills', 'receipt', status],
    queryFn: () => api<ReceiptSummary[]>(`/api/v1/receipts${query}`),
  })

  const costs = useQuery({
    queryKey: ['purchase-bills', 'cost', status],
    queryFn: () => api<CostSummary[]>(`/api/v1/cost-invoices${query}${query ? '&' : '?'}take=200`),
  })

  const rows: Row[] = [
    ...(receipts.data ?? []).map((r) => ({
      kind: 'receipt' as const, id: r.id, no: r.receiptNo, supplierName: r.supplierName,
      invoiceNo: r.supplierInvoiceNo, date: r.supplierInvoiceDate ?? r.receiptDate, total: r.netTotal, status: r.status,
    })),
    ...(costs.data ?? []).map((c) => ({
      kind: 'cost' as const, id: c.id, no: c.invoiceNo || '—', supplierName: c.supplierName,
      invoiceNo: c.supplierInvoiceNo, date: c.supplierInvoiceDate ?? c.createdAt.slice(0, 10), total: c.total, status: c.status,
    })),
  ]
    // الملغاةُ (ومنها أصلُ كلِّ فاتورةٍ عُدّلت) تحت «ملغاة» وحدَها — لا تزحم «الكلّ».
    .filter((r) => status !== '' || r.status !== 'REVERSED')
    .sort((a, b) => b.date.localeCompare(a.date))

  return (
    <Panel title="فواتيرُ المشتريات">
      <div className="flex flex-wrap items-center gap-2">
        {can(Perm.receiptCreate) && (
          <Button tone="primary" onClick={() => navigate('/purchase-bills/new')}>+ فاتورةٌ جديدة</Button>
        )}
        <span className="mx-2 border-s border-line" />
        {([['', 'الكلّ'], ['DRAFT', 'مسوّدات'], ['POSTED', 'مُرحَّلة'], ['REVERSED', 'ملغاة']] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setStatus(value)}
            className={`min-h-10 rounded-full border px-4 text-sm ${status === value ? 'border-brand bg-brand/10 font-semibold text-brand' : 'border-line'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <ErrorBanner error={receipts.error ?? costs.error} />

      {receipts.isPending || costs.isPending ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="mt-3"><Empty>لا فواتير.</Empty></div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[42rem] text-sm">
            <thead className="text-xs text-ink-soft">
              <tr>
                <th className="p-2 text-start">رقمنا</th>
                <th className="p-2 text-start">النوع</th>
                <th className="p-2 text-start">المورّد</th>
                <th className="p-2 text-start">رقمُ فاتورته</th>
                <th className="p-2 text-start">التاريخ</th>
                <th className="p-2 text-end">المجموع</th>
                <th className="p-2 text-start">الحالة</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={`${r.kind}-${r.id}`}
                  className="cursor-pointer border-t border-line hover:bg-surface-raised"
                  onClick={() => navigate(path(r.kind, r.id))}
                >
                  <td className="tabular p-2" dir="ltr">{r.no}</td>
                  <td className="p-2 text-xs text-ink-soft">{KIND_LABEL[r.kind]}</td>
                  <td className="p-2">{r.supplierName}</td>
                  <td className="tabular p-2" dir="ltr">{r.invoiceNo ?? '—'}</td>
                  <td className="tabular p-2" dir="ltr">{r.date}</td>
                  <td className="tabular p-2 text-end font-semibold" dir="ltr">{formatMoney(r.total)}</td>
                  <td className={`p-2 ${STATUS[r.status].tone}`}>{STATUS[r.status].label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}

// ── الفاتورة ────────────────────────────────────────────────────────────────

function Editor({ kind, id }: { kind: Kind; id: string | null }) {
  const navigate = useNavigate()
  const queries = useQueryClient()
  const { session, can } = useAuth()
  const branchId = session?.me.activeBranchId ?? null

  const detail = useQuery({
    queryKey: ['purchase-bill', kind, id],
    queryFn: async () => kind === 'receipt'
      ? fromReceipt(await api<ReceiptDetail>(`/api/v1/receipts/${id}`))
      : fromCost(await api<CostDetail>(`/api/v1/cost-invoices/${id}`)),
    enabled: id !== null,
  })

  if (id !== null && detail.isPending) {
    return <Spinner />
  }

  if (detail.error) {
    return <ErrorBanner error={detail.error} />
  }

  const bill = detail.data ?? null

  if (bill && bill.status !== 'DRAFT') {
    return <Posted bill={bill} />
  }

  return (
    <Form
      key={id ?? 'new'}
      kind={kind}
      branchId={branchId}
      existing={bill}
      onSaved={(saved) => {
        void queries.invalidateQueries({ queryKey: ['purchase-bills'] })
        void queries.invalidateQueries({ queryKey: ['purchase-bill', kind, saved] })

        if (id === null) {
          navigate(path(kind, saved), { replace: true })
        }
      }}
      canPost={can(Perm.receiptPost)}
    />
  )
}

function Form({
  kind, branchId, existing, onSaved, canPost,
}: {
  kind: Kind
  branchId: string | null
  existing: Bill | null
  onSaved: (id: string) => void
  canPost: boolean
}) {
  const navigate = useNavigate()

  const suppliers = useQuery({ queryKey: ['suppliers'], queryFn: () => api<Supplier[]>('/api/v1/suppliers') })
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => api<Warehouse[]>('/api/v1/warehouses') })

  const usable = (warehouses.data ?? []).filter((w) => w.isActive && w.kind !== 'TRANSIT')

  const [supplierId, setSupplierId] = useState(existing?.supplierId ?? '')
  const [warehouseId, setWarehouseId] = useState(existing?.warehouseId ?? '')
  const [invoiceNo, setInvoiceNo] = useState(existing?.invoiceNo ?? '')
  const [invoiceDate, setInvoiceDate] = useState(existing?.date ?? today())
  const [lines, setLines] = useState<Draft[]>(
    (existing?.lines ?? []).map((l) => ({
      variantId: l.variantId, name: l.name, qty: String(l.qty), cost: String(l.unitCost),
    })),
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [duplicate, setDuplicate] = useState(false)

  const warehouse = warehouseId || (usable.length === 1 ? usable[0].id : '')

  const total = lines.reduce((sum, l) => sum + (toNumber(l.qty) ?? 0) * (toNumber(l.cost) ?? 0), 0)
  const valid = supplierId !== '' && warehouse !== '' && lines.length > 0
    && lines.every((l) => (toNumber(l.qty) ?? 0) > 0 && (toNumber(l.cost) ?? -1) >= 0)

  /** يحفظ المسوّدة — يُنشئها أوّلَ مرّة — ويردّ مُعرِّفَها. */
  const save = async (): Promise<string> => {
    let billId = existing?.id ?? null

    if (billId === null) {
      const created = kind === 'receipt'
        ? (await api<ReceiptSummary>('/api/v1/receipts', {
            method: 'POST',
            body: {
              supplierId,
              warehouseId: warehouse,
              receiptDate: invoiceDate || today(),
              supplierInvoiceNo: invoiceNo.trim() || null,
              supplierInvoiceDate: invoiceDate || null,
            },
          })).id
        : (await api<CostDetail>('/api/v1/cost-invoices', {
            method: 'POST',
            body: {
              supplierId,
              branchId,
              supplierInvoiceNo: invoiceNo.trim() || null,
              supplierInvoiceDate: invoiceDate || null,
            },
          })).header.id

      billId = created
    }

    await api(`${base(kind)}/${billId}/lines`, {
      method: 'PUT',
      body: {
        lines: lines.map((l) => ({
          variantId: l.variantId,
          ...(kind === 'cost' ? { warehouseId: warehouse } : {}),
          qty: toNumber(l.qty) ?? 0,
          unitCost: toNumber(l.cost) ?? 0,
        })),
      },
    })

    return billId
  }

  const run = async (post: boolean, acknowledge = false) => {
    setBusy(true)
    setError(null)

    try {
      const billId = await save()

      if (post) {
        await api(`${base(kind)}/${billId}/post`, {
          method: 'POST',
          body: acknowledge
            ? { acknowledgeDuplicate: true, duplicateReason: 'فاتورةٌ ثانيةٌ بالرقم نفسِه من المورّد' }
            : {},
        })
      }

      setDuplicate(false)
      onSaved(billId)

      if (post) {
        navigate(path(kind, billId))
      }
    } catch (failure) {
      if (failure instanceof ApiError && failure.code === 'purch.duplicate_supplier_invoice') {
        setDuplicate(true)
      }

      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const title = existing
    ? `فاتورةُ ${KIND_LABEL[kind]} ${existing.no} — مسوّدة`
    : 'فاتورةُ مشترياتٍ جديدة'

  return (
    <div className="grid gap-4">
      <Panel title={title}>
        {kind === 'cost' && (
          <Banner tone="info">
            فاتورةُ تسعير: تُسعّر بضاعةً <b>دخلت المخزن من قبل</b> ولا تُدخل كمّيّة. صنفٌ لم يدخل المخزنَ بعد؟ أدخِله
            بـ«فاتورةٌ جديدة» من قائمة فواتير المشتريات.
          </Banner>
        )}
        {existing?.notes && <p className="mb-2 text-sm text-ink-soft">{existing.notes}</p>}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="المورّد">
            <Select value={supplierId} onChange={setSupplierId}>
              <option value="">— اختر —</option>
              {(suppliers.data ?? []).filter((s) => s.isActive).map((s) => (
                <option key={s.id} value={s.id}>{s.nameAr}</option>
              ))}
            </Select>
          </Field>

          {usable.length > 1 && (
            <Field label="المستودع">
              <Select value={warehouseId} onChange={setWarehouseId}>
                <option value="">— اختر —</option>
                {usable.map((w) => <option key={w.id} value={w.id}>{w.nameAr}</option>)}
              </Select>
            </Field>
          )}

          <Field label="رقمُ فاتورة المورّد" hint="اختياريّ — ويمنع إدخالَ الفاتورة مرّتين">
            <TextInput value={invoiceNo} onChange={setInvoiceNo} placeholder="مثلًا 1542" />
          </Field>

          <Field label="تاريخُ الفاتورة">
            <TextInput value={invoiceDate} onChange={setInvoiceDate} placeholder="2026-10-23" />
          </Field>
        </div>
      </Panel>

      <Panel title="الأصناف">
        <ItemSearch
          branchId={branchId}
          warehouseId={warehouse}
          onPick={(item) => setLines((current) => current.some((l) => l.variantId === item.variantId)
            ? current
            : [...current, { variantId: item.variantId, name: `${item.productName}${item.label ? ` · ${item.label}` : ''}`, qty: '1', cost: '' }])}
        />

        {lines.length === 0 ? (
          <div className="mt-3"><Empty>ابحث عن صنفٍ وأضِفه.</Empty></div>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[36rem] text-sm">
              <thead className="text-xs text-ink-soft">
                <tr>
                  <th className="p-2 text-start">الصنف</th>
                  <th className="p-2 text-center">العدد</th>
                  <th className="p-2 text-center">سعرُ الحبّة</th>
                  <th className="p-2 text-end">المجموع</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const lineTotal = (toNumber(line.qty) ?? 0) * (toNumber(line.cost) ?? 0)
                  const update = (patch: Partial<Draft>) =>
                    setLines((current) => current.map((l, i) => (i === index ? { ...l, ...patch } : l)))

                  return (
                    <tr key={line.variantId} className="border-t border-line">
                      <td className="p-2">{line.name}</td>
                      <td className="w-24 p-2"><NumberInput value={line.qty} onChange={(qty) => update({ qty })} /></td>
                      <td className="w-28 p-2"><NumberInput value={line.cost} onChange={(cost) => update({ cost })} placeholder="0.000" /></td>
                      <td className="tabular p-2 text-end font-semibold" dir="ltr">{formatMoney(lineTotal)}</td>
                      <td className="p-2 text-end">
                        <button
                          type="button"
                          className="text-xs text-danger underline"
                          onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                        >
                          احذف
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-surface-raised p-3">
          <span>المجموع ({lines.length} صنف)</span>
          <b className="tabular text-2xl" dir="ltr">{formatMoney(total)}</b>
        </div>

        <ErrorBanner error={error} />

        {duplicate && (
          <Banner tone="warn">
            رقمُ هذه الفاتورة مُدخلٌ من قبل لنفس المورّد. إذا كانت فعلًا فاتورةً ثانية بالرقم نفسه:{' '}
            <button type="button" className="underline" disabled={busy} onClick={() => void run(true, true)}>
              رحّلها مع ذلك
            </button>
          </Banner>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <Button disabled={busy || !valid} onClick={() => void run(false)}>
            {busy ? 'يُحفظ…' : 'احفظ مسوّدة'}
          </Button>
          {canPost && (
            <Button tone="primary" disabled={busy || !valid} onClick={() => void run(true)}>
              {kind === 'receipt' ? 'رحّل — تدخل البضاعةُ وتُسجَّل على المورّد' : 'رحّل — تُسجَّل على المورّد'}
            </Button>
          )}
          <Button onClick={() => navigate('/purchase-bills')}>رجوع</Button>
        </div>
      </Panel>
    </div>
  )
}

function ItemSearch({
  branchId, warehouseId, onPick,
}: {
  branchId: string | null
  warehouseId: string
  onPick: (item: CatalogItem) => void
}) {
  const [term, setTerm] = useState('')

  const catalog = useQuery({
    queryKey: ['bill-catalog', branchId, warehouseId],
    queryFn: () => api<CatalogItem[]>(`/api/v1/pos/catalog?branchId=${branchId}&warehouseId=${warehouseId}`),
    enabled: branchId !== null && warehouseId !== '',
    staleTime: 5 * 60_000,
  })

  const hits = useMemo(() => {
    const needle = normalize(term)

    if (needle.length < 2) {
      return []
    }

    return (catalog.data ?? [])
      .filter((item) =>
        normalize(`${item.productName} ${item.label}`).includes(needle)
        || item.sku.toLowerCase() === needle
        || item.barcodes.includes(term.trim()))
      .slice(0, 12)
  }, [catalog.data, term])

  return (
    <div>
      <Field label="أضِف صنفًا" hint="بالاسم أو الطراز أو الباركود. صنفٌ جديدٌ لم يُعرَّف بعد؟ أضِفه من «الأصناف» أوّلًا.">
        <TextInput value={term} onChange={setTerm} placeholder={warehouseId ? 'مثلًا K216 أو باوربانك' : 'اختر المستودعَ أوّلًا'} />
      </Field>

      {catalog.isPending && warehouseId !== '' && term.trim().length > 1 && <Spinner />}

      {hits.length > 0 && (
        <ul className="mt-2 grid gap-1 rounded-lg border border-line p-1">
          {hits.map((item) => (
            <li key={item.variantId}>
              <button
                type="button"
                className="flex w-full items-baseline gap-2 rounded-md px-2 py-1 text-start text-sm hover:bg-surface-raised"
                onClick={() => {
                  onPick(item)
                  setTerm('')
                }}
              >
                <span className="min-w-0 flex-1 truncate">
                  {item.productName}
                  {item.label && <span className="text-xs text-ink-soft"> · {item.label}</span>}
                </span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{item.sku}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── المُرحَّلة والملغاة ─────────────────────────────────────────────────────

function Posted({ bill }: { bill: Bill }) {
  const navigate = useNavigate()
  const queries = useQueryClient()
  const { can } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')

  const amend = async () => {
    setBusy(true)
    setError(null)

    try {
      const copy = await api<{ header: { id: string } }>(`${base(bill.kind)}/${bill.id}/amend`, { method: 'POST' })
      await queries.invalidateQueries({ queryKey: ['purchase-bills'] })
      await queries.invalidateQueries({ queryKey: ['purchase-bill', bill.kind, bill.id] })
      navigate(path(bill.kind, copy.header.id))
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const cancel = async () => {
    setBusy(true)
    setError(null)

    try {
      await api(`${base(bill.kind)}/${bill.id}/reverse`, { method: 'POST', body: { reason: reason.trim() } })
      await Promise.all([
        queries.invalidateQueries({ queryKey: ['purchase-bills'] }),
        queries.invalidateQueries({ queryKey: ['purchase-bill', bill.kind, bill.id] }),
      ])
      setCancelling(false)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel title={`فاتورةُ ${KIND_LABEL[bill.kind]} ${bill.no} — ${STATUS[bill.status].label}`}>
      <div className="grid gap-1 text-sm sm:grid-cols-2">
        <p>المورّد: <b>{bill.supplierName}</b></p>
        <p>رقمُ فاتورته: <b className="tabular" dir="ltr">{bill.invoiceNo ?? '—'}</b></p>
        <p>التاريخ: <b className="tabular" dir="ltr">{bill.date}</b></p>
        {bill.notes && <p className="text-ink-soft">{bill.notes}</p>}
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[32rem] text-sm">
          <thead className="text-xs text-ink-soft">
            <tr>
              <th className="p-2 text-start">الصنف</th>
              <th className="p-2 text-center">العدد</th>
              <th className="p-2 text-center">سعرُ الحبّة</th>
              <th className="p-2 text-end">المجموع</th>
            </tr>
          </thead>
          <tbody>
            {bill.lines.map((l) => (
              <tr key={l.id} className="border-t border-line">
                <td className="p-2">{l.name}</td>
                <td className="tabular p-2 text-center">{l.qty}</td>
                <td className="tabular p-2 text-center" dir="ltr">{formatMoney(l.unitCost)}</td>
                <td className="tabular p-2 text-end" dir="ltr">{formatMoney(l.total)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-line font-bold">
              <td className="p-2" colSpan={3}>المجموع</td>
              <td className="tabular p-2 text-end" dir="ltr">{formatMoney(bill.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <ErrorBanner error={error} />

      {bill.status === 'POSTED' && can(Perm.receiptReverse) && (
        <div className="mt-3 grid gap-2">
          <div className="flex flex-wrap gap-2">
            <Button tone="primary" disabled={busy} onClick={() => void amend()}>
              عدّل الفاتورة
            </Button>
            <Button disabled={busy} onClick={() => setCancelling((v) => !v)}>ألغِ الفاتورة</Button>
            <Button onClick={() => navigate('/purchase-bills')}>رجوع</Button>
          </div>
          <p className="text-xs text-ink-soft">
            «عدّل» تُلغي هذه الفاتورة وتفتح نسخةً منها مسوّدةً لتصحّحها وترحّلها — والأصلُ يبقى في القائمة «ملغاة».
          </p>

          {cancelling && (
            <div className="grid gap-2 rounded-lg border border-line p-3">
              <TextInput value={reason} onChange={setReason} placeholder="سببُ الإلغاء (١٠ أحرفٍ على الأقلّ)" />
              <Button tone="primary" disabled={busy || reason.trim().length < 10} onClick={() => void cancel()}>
                {bill.kind === 'receipt'
                  ? 'تأكيدُ الإلغاء — تخرج البضاعةُ وتنقص ذمّةُ المورّد'
                  : 'تأكيدُ الإلغاء — تنقص ذمّةُ المورّد وكلفةُ البضاعة'}
              </Button>
            </div>
          )}
        </div>
      )}

      {bill.status !== 'POSTED' && (
        <div className="mt-3"><Button onClick={() => navigate('/purchase-bills')}>رجوع</Button></div>
      )}
    </Panel>
  )
}
